import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import { useSearchSuggestions, type SearchSuggestion } from "@/features/jobs";

/**
 * Hero command-prompt search (Landing v2).
 *
 * Honors the platform's existing search contract — same params, same nav —
 * but presents a single mono input with a `>` prompt glyph. Quick suggestions
 * appear as a horizontally-scrolling mono row below (snap-x on mobile).
 * Token syntax (cert:, clearance:, shift:) appears only in autocomplete hints,
 * never as the primary input affordance.
 */
const QUICK_TOKENS = [
  { label: "cert:CET", q: "critical+environment+technician" },
  { label: "clearance:TS/SCI", q: "ts+sci+cleared" },
  { label: "system:HVAC", q: "hvac" },
  { label: "system:MEP", q: "mep" },
  { label: "shift:overnight", q: "overnight" },
  { label: "trade:electrical", q: "electrician" },
  { label: "stack:fiber", q: "fiber" },
  { label: "build:vibe-coding", q: "ai+assisted+developer" },
];

export function HeroConsoleSearch() {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data: suggestions } = useSearchSuggestions(value, "keyword");

  const submit = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      const params = new URLSearchParams();
      if (trimmed) params.set("q", trimmed);
      navigate(`/jobs${params.toString() ? `?${params.toString()}` : ""}`);
    },
    [navigate]
  );

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      submit(value);
    },
    [value, submit]
  );

  const onSelectSuggestion = (s: SearchSuggestion) => {
    setValue(s.searchValue);
    setOpen(false);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const hasSuggestions =
    !!suggestions && (suggestions.titles.length > 0 || suggestions.workTypes.length > 0);

  return (
    <div ref={wrapperRef} className="w-full max-w-2xl">
      <form
        onSubmit={onSubmit}
        role="search"
        aria-label="Search jobs by role, certification, clearance, or system"
        className="relative"
      >
        <label htmlFor="hero-console-input" className="sr-only">
          Search roles, certifications, clearances, or systems
        </label>
        <div
          className={[
            "group relative flex items-center gap-3 rounded-md",
            "border border-[hsl(var(--surface-instrument-fg)/0.18)]",
            "bg-[hsl(var(--surface-instrument)/0.7)] backdrop-blur-md",
            "px-4 py-3.5 transition-colors",
            "focus-within:border-[hsl(var(--heat-4)/0.6)]",
            "focus-within:shadow-[0_0_0_3px_hsl(var(--heat-4)/0.12)]",
          ].join(" ")}
        >
          <span
            aria-hidden="true"
            className="mono-meta select-none text-[hsl(var(--heat-4))] text-base"
          >
            &gt;
          </span>
          <input
            id="hero-console-input"
            type="text"
            inputMode="search"
            autoComplete="off"
            placeholder={
              typeof window !== "undefined" && window.matchMedia("(min-width: 640px)").matches
                ? "search natural language, e.g. critical facilities tech, Atlanta, overnight"
                : "search jobs, skills, locations…"
            }
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            className={[
              "mono-meta flex-1 bg-transparent outline-none min-w-0",
              "text-[15px] text-[hsl(var(--surface-instrument-fg))]",
              "placeholder:text-[hsl(var(--surface-instrument-fg)/0.45)]",
              "placeholder:font-normal placeholder:text-[14px]",
            ].join(" ")}
          />
          <button
            type="submit"
            className={[
              "mono-meta shrink-0 rounded px-3 py-1.5 text-xs uppercase tracking-wider",
              "bg-[hsl(var(--heat-4))] text-[hsl(222_32%_5%)]",
              "transition-transform hover:-translate-y-px active:translate-y-0",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--heat-4)/0.5)]",
            ].join(" ")}
            aria-label="Run search"
          >
            run
          </button>
        </div>

        {open && hasSuggestions && (
          <div
            role="listbox"
            className={[
              "absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-md",
              "border border-[hsl(var(--surface-instrument-fg)/0.18)]",
              "bg-[hsl(var(--surface-instrument))] shadow-2xl",
            ].join(" ")}
          >
            <div className="max-h-72 overflow-y-auto py-1">
              {suggestions.titles.slice(0, 6).map((s) => (
                <button
                  key={`t-${s.label}`}
                  type="button"
                  role="option"
                  aria-selected="false"
                  onClick={() => onSelectSuggestion(s)}
                  className="mono-meta flex w-full items-center justify-between px-4 py-2 text-left text-[13px] text-[hsl(var(--surface-instrument-fg)/0.92)] transition-colors hover:bg-[hsl(var(--surface-instrument-fg)/0.06)]"
                >
                  <span>
                    <span className="text-[hsl(var(--heat-4))]">role:</span> {s.label}
                  </span>
                  <span className="tabular-nums text-[hsl(var(--surface-instrument-fg)/0.5)]">
                    {s.count}
                  </span>
                </button>
              ))}
              {suggestions.workTypes.slice(0, 4).map((s) => (
                <button
                  key={`w-${s.label}`}
                  type="button"
                  role="option"
                  aria-selected="false"
                  onClick={() => onSelectSuggestion(s)}
                  className="mono-meta flex w-full items-center justify-between px-4 py-2 text-left text-[13px] text-[hsl(var(--surface-instrument-fg)/0.92)] transition-colors hover:bg-[hsl(var(--surface-instrument-fg)/0.06)]"
                >
                  <span>
                    <span className="text-[hsl(var(--heat-4))]">work:</span> {s.label}
                  </span>
                  <span className="tabular-nums text-[hsl(var(--surface-instrument-fg)/0.5)]">
                    {s.count}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </form>

      {/* Quick token row — wraps on every viewport so chips never clip */}
      <div
        className="mt-4 flex flex-wrap gap-x-2 gap-y-2"
        style={{ flexWrap: "wrap" }}
        aria-label="Suggested search tokens"
      >
        {QUICK_TOKENS.map((t) => (
          <button
            key={t.label}
            type="button"
            onClick={() => navigate(`/jobs?q=${t.q}`)}
            className={[
              "mono-meta shrink-0 rounded-sm border px-2.5 py-1 text-[11px]",
              "border-[hsl(var(--surface-instrument-fg)/0.18)]",
              "bg-[hsl(var(--surface-instrument)/0.5)]",
              "text-[hsl(var(--surface-instrument-fg)/0.78)]",
              "transition-colors hover:border-[hsl(var(--heat-4)/0.5)] hover:text-[hsl(var(--heat-4))]",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--heat-4)/0.4)]",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}
